// Share-link mutations (PRD §9.5 + docs/frontend_architecture.md §5,
// B2 PR2 / B2b + B2 PR4 / B2d + B2 PR5 / B2e).
//
// `useShareAccess(token)` is the public-viewer counterpart to `useLogin`:
// it POSTs to /share/{token}/access with a (password, email) pair and
// resolves to the strongly-typed payload (link + scope + candidate or
// playlist). The hook does not write any cache itself — the calling
// route holds the access result in component state and only persists it
// for the lifetime of the viewer session. ApiError is allowed to flow up
// untouched so the gate component can branch on status (401 password,
// 403 expired/revoked/permission, 404 unknown token, 410 expired in the
// future if backend differentiates).
//
// `usePostShareComment(token, candidateId)` posts a comment through the
// public share endpoint and applies an optimistic insert + rollback
// pattern: onMutate prepends a synthetic CommentOut to the cached list
// keyed by `shareKeys.comments(token, candidateId)`; onError restores
// the snapshot the caller saw when they hit submit; onSuccess swaps
// the synthetic placeholder for the server-canonical row. Errors flow
// up untouched (ApiError) so the form can render the detail string.
//
// `usePostShareAnnotation(token, candidateId)` mirrors the comment
// flow for annotation drafts: optimistic insert into the
// (token, candidateId, frame) annotations cache, rollback on error,
// swap-in-place on success. The mutation owns the synthetic id so
// onSuccess can find the placeholder regardless of how many other
// strokes the user has drawn between submit and the server's reply.

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { client } from '../client';
import {
  shareKeys,
  type AnnotationOut,
  type CommentOut,
} from '../queries/share';
import type { components } from '../schema';

type ShareLinkAccessRequest = components['schemas']['ShareLinkAccessRequest'];
type CommentCreate = components['schemas']['CommentCreate'];
type AnnotationCreate = components['schemas']['AnnotationCreate'];
type StrokePrimitive = components['schemas']['StrokePrimitive'];
export type ShareAccessResponse = components['schemas']['ShareAccessResponse'];

interface UseShareAccessVars {
  password?: string | null;
  email?: string | null;
}

async function postShareAccess(
  token: string,
  vars: UseShareAccessVars
): Promise<ShareAccessResponse> {
  // ShareLinkAccessRequest accepts `null` on both fields — pass through
  // as-is so the backend can distinguish "field absent" from "field
  // explicitly cleared." Pydantic treats `undefined` and `null` the same
  // here, but openapi-fetch is happier omitting `undefined`.
  const body: ShareLinkAccessRequest = {
    password: vars.password ?? null,
    email: vars.email ?? null,
  };
  const { data } = await client.POST('/api/v1/share/{token}/access', {
    params: { path: { token } },
    body,
  });
  if (!data) throw new Error('useShareAccess: empty response');
  return data;
}

export function useShareAccess(
  token: string
): UseMutationResult<ShareAccessResponse, Error, UseShareAccessVars> {
  return useMutation<ShareAccessResponse, Error, UseShareAccessVars>({
    mutationFn: (vars) => postShareAccess(token, vars),
  });
}

export interface UsePostShareCommentVars {
  body: string;
  frame?: number | null;
  timecode_seconds?: number | null;
  parent_id?: string | null;
  annotation_id?: string | null;
  author_display_name?: string | null;
}

interface PostShareCommentContext {
  /** Snapshot of the cached list at onMutate time, restored on error. */
  previous: CommentOut[] | undefined;
  /** Synthetic id used for the optimistic row so onSuccess can find it. */
  optimisticId: string;
}

async function postShareComment(
  token: string,
  candidateId: string,
  vars: UsePostShareCommentVars,
  password: string | null
): Promise<CommentOut> {
  const body: CommentCreate = {
    body: vars.body,
    frame: vars.frame ?? null,
    timecode_seconds: vars.timecode_seconds ?? null,
    parent_id: vars.parent_id ?? null,
    annotation_id: vars.annotation_id ?? null,
    author_display_name: vars.author_display_name ?? null,
  };
  const { data } = await client.POST(
    '/api/v1/share/{token}/candidates/{candidate_id}/comments',
    {
      params: {
        path: { token, candidate_id: candidateId },
      },
      headers: password !== null ? { 'X-Share-Password': password } : {},
      body,
    }
  );
  if (!data) throw new Error('usePostShareComment: empty response');
  return data;
}

/**
 * Generate a non-UUID id for the optimistic row so we can identify
 * (and replace) it on success without colliding with any real
 * server-issued UUID. crypto.randomUUID would work but a `tmp-…`
 * sentinel is unmistakable in devtools and tests.
 */
function tempCommentId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function usePostShareComment(
  token: string,
  candidateId: string,
  password: string | null = null
): UseMutationResult<
  CommentOut,
  Error,
  UsePostShareCommentVars,
  PostShareCommentContext
> {
  const queryClient = useQueryClient();
  const key = shareKeys.comments(token, candidateId);
  return useMutation<
    CommentOut,
    Error,
    UsePostShareCommentVars,
    PostShareCommentContext
  >({
    mutationFn: (vars) => postShareComment(token, candidateId, vars, password),
    onMutate: async (vars) => {
      // Cancel in-flight refetches so a stale list doesn't clobber our
      // optimistic insert mid-mutation.
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CommentOut[]>(key);
      const optimisticId = tempCommentId();
      const now = new Date().toISOString();
      const optimistic: CommentOut = {
        id: optimisticId,
        created_at: now,
        updated_at: now,
        candidate_id: candidateId,
        author_id: null,
        author_display_name: vars.author_display_name ?? 'You',
        body: vars.body,
        frame: vars.frame ?? null,
        timecode_seconds: vars.timecode_seconds ?? null,
        parent_id: vars.parent_id ?? null,
        thread_root_id: null,
        annotation_id: vars.annotation_id ?? null,
        is_resolved: false,
        resolved_at: null,
        resolved_by_id: null,
        source: null,
      };
      queryClient.setQueryData<CommentOut[]>(key, (prev) =>
        prev ? [...prev, optimistic] : [optimistic]
      );
      return { previous, optimisticId };
    },
    onError: (_err, _vars, context) => {
      if (context !== undefined) {
        queryClient.setQueryData<CommentOut[]>(key, context.previous);
      }
    },
    onSuccess: (saved, _vars, context) => {
      // Swap the placeholder for the server-canonical row in place so
      // the rendered order doesn't shift. If the cache was missing
      // (e.g. list query never mounted), seed it with [saved].
      queryClient.setQueryData<CommentOut[]>(key, (prev) => {
        if (!prev) return [saved];
        const idx = prev.findIndex((c) => c.id === context.optimisticId);
        if (idx === -1) return [...prev, saved];
        const next = prev.slice();
        next[idx] = saved;
        return next;
      });
    },
  });
}

export interface UsePostShareAnnotationVars {
  frame: number;
  strokes_json: StrokePrimitive[];
  timecode_seconds?: number | null;
  author_display_name?: string | null;
}

interface PostShareAnnotationContext {
  previous: AnnotationOut[] | undefined;
  optimisticId: string;
}

async function postShareAnnotation(
  token: string,
  candidateId: string,
  vars: UsePostShareAnnotationVars,
  password: string | null
): Promise<AnnotationOut> {
  const body: AnnotationCreate = {
    frame: vars.frame,
    strokes_json: vars.strokes_json,
    timecode_seconds: vars.timecode_seconds ?? null,
    author_display_name: vars.author_display_name ?? null,
  };
  const { data } = await client.POST(
    '/api/v1/share/{token}/candidates/{candidate_id}/annotations',
    {
      params: {
        path: { token, candidate_id: candidateId },
      },
      headers: password !== null ? { 'X-Share-Password': password } : {},
      body,
    }
  );
  if (!data) throw new Error('usePostShareAnnotation: empty response');
  return data;
}

function tempAnnotationId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function usePostShareAnnotation(
  token: string,
  candidateId: string,
  password: string | null = null
): UseMutationResult<
  AnnotationOut,
  Error,
  UsePostShareAnnotationVars,
  PostShareAnnotationContext
> {
  const queryClient = useQueryClient();
  return useMutation<
    AnnotationOut,
    Error,
    UsePostShareAnnotationVars,
    PostShareAnnotationContext
  >({
    mutationFn: (vars) => postShareAnnotation(token, candidateId, vars, password),
    onMutate: async (vars) => {
      // Optimistic insert lands in the frame-keyed bucket the user is
      // currently looking at. If the user switches frames before the
      // POST resolves, onSuccess will still find the placeholder by id
      // — but a frame-swap viewer doesn't see the optimistic row, only
      // the canonical one once the cache for that frame mounts.
      const key = shareKeys.annotations(token, candidateId, vars.frame);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AnnotationOut[]>(key);
      const optimisticId = tempAnnotationId();
      const now = new Date().toISOString();
      const optimistic: AnnotationOut = {
        id: optimisticId,
        created_at: now,
        updated_at: now,
        candidate_id: candidateId,
        author_id: null,
        author_display_name: vars.author_display_name ?? 'You',
        frame: vars.frame,
        timecode_seconds: vars.timecode_seconds ?? null,
        strokes_json: vars.strokes_json,
        snapshot_s3_key: null,
        review_session_id: null,
      };
      queryClient.setQueryData<AnnotationOut[]>(key, (prev) =>
        prev ? [...prev, optimistic] : [optimistic]
      );
      return { previous, optimisticId };
    },
    onError: (_err, vars, context) => {
      if (context === undefined) return;
      const key = shareKeys.annotations(token, candidateId, vars.frame);
      queryClient.setQueryData<AnnotationOut[]>(key, context.previous);
    },
    onSuccess: (saved, vars, context) => {
      const key = shareKeys.annotations(token, candidateId, vars.frame);
      queryClient.setQueryData<AnnotationOut[]>(key, (prev) => {
        if (!prev) return [saved];
        const idx = prev.findIndex((a) => a.id === context.optimisticId);
        if (idx === -1) return [...prev, saved];
        const next = prev.slice();
        next[idx] = saved;
        return next;
      });
    },
  });
}
